// Large-scale version of the overlay top bar's fairy dust. Soft glowing orbs
// rising from below, drifting up and fading. Much bigger spread + size than the
// overlay version since this covers a full-viewport hero instead of a strip.
// Uses jopFairyDust keyframe (already in index.css).
import { useMemo } from "react";

const COLORS = [
  "var(--jop-gold)",
  "var(--jop-cream)",
  "var(--jop-highlight-blue)",
  "var(--jop-gold)",        // gold weighted higher
  "var(--jop-gold)",
];

function rand(seed) {
  let s = seed;
  return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
}

/**
 * @param {number} density — how many dust particles (default 60)
 * @param {number} seed    — RNG seed for stable placement
 * @param {number} riseDistance — px travel before fade out (larger = bigger hero)
 */
export default function FairyDustField({ density = 60, seed = 3, riseDistance = 240 }) {
  const dust = useMemo(() => {
    const r = rand(seed);
    return Array.from({ length: density }, (_, i) => {
      const left = r() * 100;
      const bottom = r() * 100; // start positions distributed vertically
      const size = 3 + r() * 9; // 3px to 12px
      const delay = r() * 10;
      const dur = 5 + r() * 7;  // 5-12s
      const color = COLORS[Math.floor(r() * COLORS.length)];
      const peak = 0.4 + r() * 0.55; // 0.4–0.95
      const xDrift = (r() - 0.5) * 30; // horizontal drift left/right
      return { left, bottom, size, delay, dur, color, peak, xDrift, key: i };
    });
  }, [density, seed]);

  return (
    <>
      <style>{`
        /* Linear Y progression: travel % matches time % at every keyframe so
         * motion never decelerates to a stop. Pair with linear timing. */
        @keyframes jopFairyDustLarge {
          0%   { opacity: 0;                                  transform: translate(0, 0) scale(0.55); }
          10%  { opacity: var(--peak, 0.7);                   transform: translate(calc(var(--xd, 0px) * 0.10), calc(var(--rise, -120px) * 0.10)) scale(0.85); }
          25%  { opacity: calc(var(--peak, 0.7) * 0.95);      transform: translate(calc(var(--xd, 0px) * 0.25), calc(var(--rise, -120px) * 0.25)) scale(1); }
          50%  { opacity: calc(var(--peak, 0.7) * 0.85);      transform: translate(calc(var(--xd, 0px) * 0.50), calc(var(--rise, -120px) * 0.50)) scale(1); }
          75%  { opacity: calc(var(--peak, 0.7) * 0.50);      transform: translate(calc(var(--xd, 0px) * 0.75), calc(var(--rise, -120px) * 0.75)) scale(0.9); }
          92%  { opacity: calc(var(--peak, 0.7) * 0.15);      transform: translate(calc(var(--xd, 0px) * 0.92), calc(var(--rise, -120px) * 0.92)) scale(0.6); }
          100% { opacity: 0;                                  transform: translate(var(--xd, 0px), var(--rise, -120px)) scale(0.35); }
        }
      `}</style>
      <div style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        zIndex: 1,
      }}>
        {dust.map((d) => (
          <span
            key={d.key}
            style={{
              position: "absolute",
              left: `${d.left}%`,
              bottom: `${d.bottom}%`,
              width: `${d.size}px`,
              height: `${d.size}px`,
              borderRadius: "50%",
              background: d.color,
              boxShadow: `0 0 ${d.size + 4}px ${d.color}, 0 0 ${d.size * 2}px ${d.color}`,
              opacity: 0,
              animation: `jopFairyDustLarge ${d.dur}s linear ${d.delay}s infinite`,
              "--peak": d.peak,
              "--rise": `${-riseDistance}px`,
              "--xd": `${d.xDrift}px`,
            }}
          />
        ))}
      </div>
    </>
  );
}
